import { ReactElement, useState } from 'react';
import {
  Export,
  IOption,
  LinkButton,
  Refresh,
  Spinner,
  Tile,
  TileVariant,
  Typography,
  XIcon,
} from '@weareopenr/component-library';
import { Routes } from 'src/routes';
import { useTimetable } from 'src/hooks/useTimetable';
import { SpinnerContainer } from 'src/components/SpinnerContainer';
import { ITimetableEntityGroup } from 'src/utils/timetables/groupSchedulesByEntity';
import { getTrack, TimetableScheduleTrack } from 'src/utils/timetables/helpers';
import styles from './TimetableEntitiesTrayContent.module.scss';

export interface TimetableEntitiesTrayContentProps {
  timetableId: string;
  canEdit: boolean;
  onChangeDefault: (track: TimetableScheduleTrack) => void;
  onRemoveEntity: (group: ITimetableEntityGroup) => void;
  onClose: () => void;
}

interface EntityListConfig {
  heading: string;
  variant: TileVariant;
  openLabel: string;
  changeDefaultLabel: string;
  getPath: (entityId: string) => string;
}

// Tile variants mirror TimetableDay's lanes, so an entity looks the same here as on the grid.
const ENTITY_LISTS: Record<TimetableScheduleTrack, EntityListConfig> = {
  menu: {
    heading: 'Menus in Timetable:',
    variant: 'muted',
    openLabel: 'Open in menu builder',
    changeDefaultLabel: 'Change default menu',
    getPath: (entityId) => `${Routes.MENUS}/${entityId}`,
  },
  price_scheme: {
    heading: 'Price Schemes in Timetable:',
    variant: 'bright',
    openLabel: 'Open price scheme',
    changeDefaultLabel: 'Change default price scheme',
    getPath: (entityId) => `${Routes.PRICE_SCHEMES}/${entityId}`,
  },
};

// Menu sets share the menu list, but open in their own builder.
const MENU_SET_LINK = {
  openLabel: 'Open menu set',
  getPath: (entityId: string) => `${Routes.MENU_SETS}/${entityId}`,
};

const TRACK_ORDER: TimetableScheduleTrack[] = ['menu', 'price_scheme'];

export const TimetableEntitiesTrayContent = ({
  timetableId,
  canEdit,
  onChangeDefault,
  onRemoveEntity,
  onClose,
}: TimetableEntitiesTrayContentProps): ReactElement => {
  const { data, byEntity, isLoading } = useTimetable(timetableId);
  // A menu item click bubbles (through React's portal) back up to the Tile, which re-opens its
  // menu - so, as TimetableDay does, the tiles are remounted after any action to drop that state.
  const [tileResetCount, setTileResetCount] = useState(0);

  if (isLoading || !data) {
    return (
      <SpinnerContainer>
        <Spinner size="large" colour="rainbow" />
      </SpinnerContainer>
    );
  }

  const withTileReset = (action: () => void) => () => {
    setTileResetCount((count) => count + 1);
    action();
  };

  const openInNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const getActions = (group: ITimetableEntityGroup): IOption[] => {
    const config = ENTITY_LISTS[getTrack(group.entityType)];
    const link = group.entityType === 'menuSet' ? MENU_SET_LINK : config;
    const actions: IOption[] = [
      {
        id: 'open',
        name: link.openLabel,
        icon: Export,
        onOptionClick: withTileReset(() => openInNewTab(link.getPath(group.entityId))),
      },
    ];

    if (!canEdit) return actions;

    if (group.isDefault) {
      actions.push({
        id: 'change-default',
        name: config.changeDefaultLabel,
        icon: Refresh,
        onOptionClick: withTileReset(() => onChangeDefault(getTrack(group.entityType))),
      });
    } else {
      actions.push({
        id: 'remove',
        name: 'Remove from timetable',
        icon: XIcon,
        hoverColour: 'tomato',
        onOptionClick: withTileReset(() => onRemoveEntity(group)),
      });
    }

    return actions;
  };

  return (
    <div className={styles['container']}>
      <div className={styles['lists']}>
        {TRACK_ORDER.map((track) => {
          const config = ENTITY_LISTS[track];
          const groups = byEntity.filter((group) => getTrack(group.entityType) === track);

          return (
            <section key={track} className={styles['section']} aria-label={config.heading}>
              <Typography size="medium" weight="semi-bold">
                {config.heading}
              </Typography>
              <div className={styles['list']}>
                {groups.map((group) => (
                  <Tile
                    key={`${group.entityId}-${tileResetCount}`}
                    mainText={group.name}
                    secondaryText={group.daysLabel}
                    colour={group.colour}
                    variant={config.variant}
                    hasAccent
                    label={group.isDefault ? 'Default' : undefined}
                    actions={getActions(group)}
                    fixRightContent
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
      <div className={styles['actions']}>
        <LinkButton type="button" onClick={onClose}>
          Close
        </LinkButton>
      </div>
    </div>
  );
};

export default TimetableEntitiesTrayContent;
